/**
 * Parses structured webinar markdown content into discrete sections.
 */

export interface WebinarMeta {
  date: string | null;
  time: string | null;
  industry: string | null;
  language: string | null;
}

export interface WebinarSpeaker {
  name: string;
  role: string;
  bio: string;
  imageUrl: string | null;
}

export interface AgendaItem {
  timestamp: string;
  label: string;
}

export interface ParsedWebinarContent {
  meta: WebinarMeta;
  subtitle: string;
  introHtml: string;
  problemFraming: string;
  whatYoullLearn: (string | { parent: string; children: string[] })[];
  /** Bullets under a "Key Takeaways" / "Conclusiones clave" heading */
  keyTakeaways: string[];
  whoShouldAttend: { title: string; items: string[] };
  speakers: WebinarSpeaker[];
  agenda: AgendaItem[];
  aboutTheHosts: string;
  ourSupport: string;
  aboutPartner: string;
  partnerTitle: string;
  /** Fallback: cleaned generic body when structured sections are mostly empty */
  genericBody: string;
}

// Heading patterns for each known section
const LEARN_PATTERN = /#{2,4}\s+(?:What (?:you[''\u2019]ll|will you) learn|(?:¿)?Qu[eé] (?:vas a aprender|aprender[aá]s)|您将学[到习](?:什么)?|你将学[到习](?:什么)?)\??\s*/i;
const TAKEAWAYS_PATTERN = /#{2,4}\s+(?:\d+\s+)?(?:Key [Tt]akeaways|(?:Conclusiones|Claves|Ideas) clave|Claves|核心要点|关键要点)\s*/i;
const ABOUT_PATTERN = /#{2,4}\s+(?:What is it about|About this webinar|Description|De qu[eé] trata|关于本次\s*(?:webinar|网络研讨会)?|本次\s*webinar\s*简介|关于此次\s*(?:webinar|网络研讨会)?)\??\s*/i;
const WHO_PATTERN = /#{2,4}\s+(?:Who [Ss]hould [Aa]ttend|(?:¿)?A qui[eé]n va dirigido|(?:¿)?Qui[eé]n deber[ií]a asistir|适合(?:参加|参与)(?:人员|的人(?:员|群))?|谁(?:应该|适合)参加)\??\s*/i;
const SPEAKERS_PATTERN = /#{2,4}\s+(?:Speakers?|Speaker Panel|Ponentes?|演讲嘉宾|主讲(?:人|嘉宾)?|讲者)\s*/i;
const AGENDA_PATTERN = /#{2,4}\s+(?:Agenda|议程|日程)\s*/i;
const HOSTS_PATTERN = /#{2,4}\s+(?:About the hosts?|Sobre los ponentes|关于主持人|关于主讲人)\s*/i;
const PARTNER_PATTERN = /#{2,4}\s+(?:About (?:Capital Cell|the partner)|Sobre (?:Capital Cell|el partner|la entidad colaboradora))\s*/i;
const SUPPORT_PATTERN = /#{2,4}\s+(?:Our support|Nuestro soporte|我们的支持)\s*/i;

/**
 * Find the start index of the first matching section heading in the content.
 * Returns -1 if not found.
 */
function findSection(content: string, pattern: RegExp): number {
  return content.search(pattern);
}

/**
 * Extract the body of a section: from after the heading match to the next ## heading or end.
 */
function extractSectionBody(content: string, pattern: RegExp, stopAtH2Only = false): string | null {
  const match = content.match(pattern);
  if (!match) return null;
  const startIdx = content.indexOf(match[0]) + match[0].length;
  const rest = content.slice(startIdx);
  // Find next heading at same or higher level (stopAtH2Only: only stop at ## not ###)
  const headingPattern = stopAtH2Only ? /\n##\s+(?!#)/ : /\n#{2,3}\s+/;
  const nextHeading = rest.search(headingPattern);
  const body = nextHeading > -1 ? rest.slice(0, nextHeading) : rest;
  return body.trim();
}

/**
 * Strip YouTube embed artifacts from text.
 */
function stripYouTubeEmbed(text: string): string {
  return text
    // Remove entire YouTube embed block: "Title | Channel - YouTube" through "Watch on" / "•Live" etc.
    .replace(/[^\n]*\|\s*[^\n]*-\s*YouTube[\s\S]*?(?:Watch on|•Live|Copy link)[\s\S]*?(?:\n\n|\n(?=#{2,4}))/gi, "")
    // Remove lines like "HP", "281K subscribers", "Search", "Info", "Shopping", "Tap to unmute", etc.
    .replace(/^(?:\d+K?\s+subscribers|Search|Info|Shopping|Tap to unmute|You're signed out|Videos you watch may.*|To avoid this.*|CancelConfirm|Include playlist|An error occurred.*|Watch later|Watch on|0:00|\/|•Live|•)$/gim, "")
    // Remove "Photo image of X" lines
    .replace(/^\[?Photo image of [^\]]*\]?\(?[^)]*\)?\s*$/gim, "")
    // Remove channel links
    .replace(/^\[[^\]]*\]\(https:\/\/www\.youtube\.com\/channel\/[^)]*\)\s*$/gim, "")
    // Remove YouTube video links that are standalone lines
    .replace(/^\[[^\]]*\]\(https:\/\/www\.youtube\.com\/watch[^)]*\)\s*$/gim, "")
    // Remove lines that are just a channel name (e.g., "HP" standalone)
    .replace(/^[A-Z][A-Za-z]{0,15}$/gm, (match) => {
      // Only remove very short standalone words that look like channel names
      const channelWords = ["HP", "IBM", "SAP", "AWS"];
      return channelWords.includes(match.trim()) ? "" : match;
    })
    // Remove "If playback doesn't begin" type messages
    .replace(/^If playback doesn't begin.*$/gim, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Strip the HubSpot header preamble that precedes actual webinar content.
 * Pattern: featured image → breadcrumb list → banner line → H1 → subtitle → CTAs → icon metadata
 */
function stripWebinarPreamble(text: string): string {
  let cleaned = text
    // Remove breadcrumb nav lists: "- [Home](...)\n- [Webinars](...)\n- Short Title"
    .replace(/^-\s+\[(?:Home|Inicio)\]\([^)]*\)\s*\n(?:-\s+\[[^\]]*\]\([^)]*\)\s*\n)*(?:-\s+[^\n]{1,80}\s*\n)?/gm, "")
    // Remove banner lines: "Upcoming webinar | date | time" or "On-demand webinar | date"
    .replace(/^(?:Upcoming|On[- ]demand|Próximo)\s+webinar\s*\\\|[^\n]*$/gim, "")
    // Remove H1 headings (title is displayed separately)
    .replace(/^#\s+[^\n]+$/gm, "")
    // Remove standalone italic subtitles directly after H1
    .replace(/^_[^_\n]{5,200}_\s*$/gm, "")
    // Remove icon metadata blocks: ![icon](url)\n\nvalue
    .replace(/!\[(?:Calander_icon|Clock_icon|Tag_icon|language_icon)[^\]]*\]\([^)]*\)\s*\n+[^\n#![\]]{1,60}\s*\n*/gi, "")
    // Remove standalone short metadata values orphaned after icon removal (e.g. "English", "45 min", "Pharma")
    // Only match very short lines that appear between blank lines
    .replace(/\n{2,}(?:English|Español|Français|Deutsch)\s*\n/gi, "\n\n")
    // Collapse excessive blank lines
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return cleaned;
}

/**
 * Clean markdown artifacts: strip images, links (keep text), CTA buttons, share blocks, etc.
 */
function cleanMarkdownBody(text: string): string {
  let cleaned = text
    // Remove full image markdown lines (handle nested parens in URLs)
    .replace(/!\[[^\]]*\]\([^)]*(?:\([^)]*\)[^)]*)*\)\s*/g, "")
    // Remove any remaining lines that are just broken image refs (e.g. ".jpg?")
    .replace(/^\.[a-z]{3,4}\?.*$/gim, "")
    // Remove bare image URLs on their own line
    .replace(/^https?:\/\/\S+\.(?:jpg|jpeg|png|gif|svg|webp)\S*$/gim, "")
    // Strip internal/anchor links but keep external links: [text](https://...) stays
    .replace(/\[([^\]]+)\]\(((?!https?:\/\/)[^)]*(?:\([^)]*\)[^)]*)*)\)/g, "$1")
    // Remove lines that are just CTA text
    .replace(/^(?:Register here|Save your spot|Watch on demand|Learn more|Watch Recording|Watch (?:the )?webinar on demand|Reserve (?:your?|you) spot|Reg[ií]strate aqu[ií]|M[aá]s informaci[oó]n|Reserva tu plaza)$/gim, "")
    // Remove CTA links like [Reserve your spot](url)
    .replace(/^\[(?:Register here|Save your spot|Watch on demand|Learn more|Watch Recording|Watch (?:the )?webinar on demand|Reserve (?:your?|you) spot|Reg[ií]strate aqu[ií]|M[aá]s informaci[oó]n|Reserva tu plaza)\]\([^)]*\)\s*$/gim, "")
    // Remove "HubSpot Video" placeholder text
    .replace(/^HubSpot Video$/gim, "")
    // Remove escaped backslashes
    .replace(/\\\\/g, "")
    // --- Strip HubSpot form artifacts ---
    // Remove form field labels with asterisks (e.g. "First name\*")
    .replace(/^[A-Za-zÀ-ÿ ]{2,30}\\\*\s*$/gm, "")
    // Remove "Please Select..." / "Selecciona..." dropdown option lists
    .replace(/^(?:Please Select|Selecciona)[^\n]*$/gm, "")
    // Remove country/option mega-lists (start with a known first option)
    .replace(/^(?:Afghanistan|Åland Islands)[^\n]*$/gm, "")
    // Remove form meta fields
    .replace(/^(?:Content Title|Content Type|Content Industry|Content Division|Select Sessions?|Would you like[^\n]*|Role|Department|Industry|Country|Cargo|Departamento|Pa[ií]s|Nombre de la empresa|Email profesional|Division \(level 2\)|¿Necesitas informaci[oó]n[^\n]*)\s*$/gim, "")
    // Remove consent paragraphs
    .replace(/^QbD Group (?:is committed to protecting|se compromete a proteger)[^\n]*$/gm, "")
    .replace(/^- (?:Yes, I agree|I consent|S[ií], acepto|Acepto la)[^\n]*$/gm, "")
    // Remove escaped asterisk lines
    .replace(/^\\\*\s*$/gm, "")
    // Remove "## Watch here" / "## Registrarse" form sections entirely
    .replace(/## (?:Watch here|Registrarse)[\s\S]*?(?=\n## )/gi, "")
    // Remove decorative/banner images
    .replace(/!\[(?:Circles|mobile-shape|Circles_quote|Circles-banner|Círculos_cita|mob-circle-shape)[^\]]*\]\([^)]*\)\s*/g, "")
    // Remove HubSpot CTA image links
    .replace(/\[?!\[Calander_icon[^\]]*\]\([^)]*\)\]?\([^)]*\)?\s*/g, "")
    // Collapse multiple blank lines
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  // Strip YouTube embed artifacts
  cleaned = stripYouTubeEmbed(cleaned);

  return cleaned;
}

export function parseWebinarContent(content: string): ParsedWebinarContent {
  const meta: WebinarMeta = { date: null, time: null, industry: null, language: null };

  // Extract meta from icon-label pairs or emoji-prefixed lines
  const dateMatch = content.match(/(?:Calander_icon|Calendar).*?\n\n(.+?)(?:\n|$)/i)
    || content.match(/📅\s*(?:Date:\s*)?(.+?)(?:\n|$)/);
  if (dateMatch) meta.date = dateMatch[1].trim();

  const timeMatch = content.match(/(?:Clock_icon|Clock).*?\n\n(.+?)(?:\n|$)/i)
    || content.match(/🕐\s*(?:Time:\s*)?(.+?)(?:\n|$)/);
  if (timeMatch) meta.time = timeMatch[1].trim();

  const tagMatch = content.match(/(?:Tag_icon|Tag).*?\n\n(.+?)(?:\n|$)/i)
    || content.match(/🏷️\s*(?:Topic:\s*)?(.+?)(?:\n|$)/);
  if (tagMatch) meta.industry = tagMatch[1].trim();

  const langMatch = content.match(/🌐\s*(?:Language:\s*)?(.+?)(?:\n|$)/)
    || content.match(/(?:language_icon|Language)\s*[:]\s*(.+?)(?:\n|$)/i);
  if (langMatch) meta.language = langMatch[1].trim();

  // Extract italic subtitle (e.g. _Session 2, Your Questions Answered_) before stripping
  const subtitleMatch = content.match(/^_([^_\n]{5,200})_\s*$/m);
  const subtitle = subtitleMatch ? subtitleMatch[1].trim() : "";

  // Strip HubSpot preamble (breadcrumbs, banner, H1, icon blocks) AFTER meta extraction
  const cleaned_content = stripWebinarPreamble(content);

  // --- Problem framing / "What is it about?" / "Description" ---
  let problemFraming = "";
  const aboutBody = extractSectionBody(cleaned_content, ABOUT_PATTERN);
  if (aboutBody) {
    problemFraming = cleanMarkdownBody(aboutBody);
  } else {
    // Fallback: extract text between first content heading and the first known section
    const knownSectionPatterns = [TAKEAWAYS_PATTERN, LEARN_PATTERN, WHO_PATTERN, SPEAKERS_PATTERN, AGENDA_PATTERN, HOSTS_PATTERN, SUPPORT_PATTERN];
    const firstKnownSection = Math.min(
      ...knownSectionPatterns
        .map((p) => findSection(cleaned_content, p))
        .filter((i) => i > -1)
    );

    if (firstKnownSection < Infinity) {
      // Find first content ## heading that isn't a known section or meta
      const skipHeadingsForFraming = /^#{2,4}\s+(?:Watch here|Register|Sign up|Stay ahead|Upcoming|Our support|Speaker|Registrarse|Ponentes?|Nuestro soporte)/i;
      const h2Regex = /^(#{2,4}\s+[^\n]+)/gm;
      let framingStart = -1;
      let match: RegExpExecArray | null;
      while ((match = h2Regex.exec(cleaned_content)) !== null) {
        if (match.index >= firstKnownSection) break;
        const heading = match[1].trim();
        if (!skipHeadingsForFraming.test(heading) && !knownSectionPatterns.some(p => p.test(heading))) {
          framingStart = match.index;
          break;
        }
      }

      // If no heading found, check for plain-text paragraphs before the first known section
      if (framingStart === -1) {
        // Find the first non-empty line of body text (skip emoji meta lines already extracted)
        // Include CJK start characters so Chinese intro paragraphs are captured as prose.
        const plainTextMatch = cleaned_content.match(/^[A-ZÀ-ÿa-z""\u4e00-\u9fff].{10,}/m);
        if (plainTextMatch && plainTextMatch.index !== undefined && plainTextMatch.index < firstKnownSection) {
          framingStart = plainTextMatch.index;
        }
      }

      if (framingStart > -1 && framingStart < firstKnownSection) {
        let rawFraming = cleaned_content.slice(framingStart, firstKnownSection);

        // Strip entire "## Watch here" form sections (including HubSpot form fields)
        rawFraming = rawFraming.replace(/## (?:Watch here|Registrarse)[\s\S]*?(?=\n## |\n$)/gi, "");

        // Strip HubSpot form field blocks
        rawFraming = rawFraming
          .replace(/^[A-Za-zÀ-ÿ ]{2,30}\\\*\s*$/gm, "")
          .replace(/^(?:Please Select|Selecciona)[^\n]*$/gm, "")
          .replace(/^(?:Afghanistan|Åland Islands)[^\n]*$/gm, "")
          .replace(/^(?:Content Title|Content Type|Content Industry|Content Division|Select Sessions?|Would you like[^\n]*|Role|Department|Industry|Country|Cargo|Departamento|Pa[ií]s|Nombre de la empresa|Email profesional|Division \(level 2\)|¿Necesitas informaci[oó]n[^\n]*)\s*$/gim, "")
          .replace(/^QbD Group (?:is committed to protecting|se compromete a proteger)[^\n]*$/gm, "")
          .replace(/^- (?:Yes, I agree|I consent|S[ií], acepto|Acepto la)[^\n]*$/gm, "")
          .replace(/^\\\*\s*$/gm, "")
          .replace(/\n{3,}/g, "\n\n")
          .trim();

        problemFraming = cleanMarkdownBody(rawFraming);
        // Strip standalone section headings (keep body text only)
        problemFraming = problemFraming
          .replace(/^#{2,4}\s+Managing regulatory[^\n]*\n*/im, "")
          .replace(/\n{3,}/g, "\n\n")
          .trim();
      }
    }
  }

  // --- What you'll learn / What will you learn ---
  const whatYoullLearn: (string | { parent: string; children: string[] })[] = [];
  const learnBody = extractSectionBody(cleaned_content, LEARN_PATTERN);
  if (learnBody) {
    const cleaned = cleanMarkdownBody(learnBody);
    const lines = cleaned.split("\n");
    let currentItem = "";
    let currentChildren: string[] = [];

    const pushCurrent = () => {
      if (currentItem) {
        if (currentChildren.length > 0) {
          whatYoullLearn.push({ parent: currentItem, children: currentChildren });
        } else {
          whatYoullLearn.push(currentItem);
        }
      }
      currentItem = "";
      currentChildren = [];
    };

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      // Check indentation on the original line (before trim)
      const isSubBullet = /^\s{2,}-\s/.test(line) && !line.startsWith("- ");

      if (!isSubBullet && trimmed.startsWith("- ")) {
        pushCurrent();
        currentItem = trimmed.replace(/^-\s+/, "").replace(/\*\*/g, "").trim();
      } else if (isSubBullet) {
        const sub = trimmed.replace(/^-\s+/, "").replace(/\*\*/g, "").trim();
        if (sub && currentItem) {
          currentChildren.push(sub);
        }
      } else if (!currentItem) {
        continue;
      }
    }
    pushCurrent();
  }

  // --- Who should attend ---
  let whoTitle = "";
  const whoItems: string[] = [];
  const whoBody = extractSectionBody(cleaned_content, WHO_PATTERN);
  if (whoBody) {
    const cleaned = cleanMarkdownBody(whoBody);
    const lines = cleaned.split("\n");
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      // Skip markdown links (CTA lines like "[Learn from our experts](...)")
      if (/^\[.*\]\(.*\)$/.test(trimmed)) continue;
      if (trimmed.startsWith("-")) {
        whoItems.push(trimmed.replace(/^-\s+/, "").trim());
      } else if (!whoTitle) {
        whoTitle = trimmed;
      } else {
        whoTitle += " " + trimmed;
      }
    }
  }

  // --- Agenda ---
  const agenda: AgendaItem[] = [];
  const agendaBody = extractSectionBody(cleaned_content, AGENDA_PATTERN);
  if (agendaBody) {
    const cleaned = cleanMarkdownBody(agendaBody);
    // Match timestamp patterns like "**1:29** – Introduction" or "**10:31** – Topic"
    const lines = cleaned.split("\n");
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      // Match: **HH:MM** – Label or **HH:MM**– Label
      const tsMatch = trimmed.match(/^\*?\*?(\d{1,2}:\d{2})\*?\*?\s*[–—-]\s*(.+)$/);
      if (tsMatch) {
        agenda.push({ timestamp: tsMatch[1], label: tsMatch[2].replace(/\*\*/g, "").trim() });
      } else if (/^-\s+/.test(trimmed)) {
        agenda.push({ timestamp: "", label: trimmed.replace(/^-\s+/, "").replace(/\*\*/g, "").trim() });
      } else if (!trimmed.startsWith("**") && !trimmed.startsWith("What to take")) {
        // Non-timestamp text like topic description without timestamp
        // Check if it's a sub-item of the agenda
        if (agenda.length > 0 && trimmed.length > 5) {
          agenda[agenda.length - 1].label += ` — ${trimmed}`;
        }
      }
    }
  }

  // --- Speakers ---
  const speakers: WebinarSpeaker[] = [];
  const speakersBody = extractSectionBody(cleaned_content, SPEAKERS_PATTERN, true);
  if (speakersBody) {
    // Split on ### or #### headings (speaker names), keeping the heading with each block
    const speakerBlocks = speakersBody.split(/(?=#{3,4}\s+[A-Z])/);
    for (const block of speakerBlocks) {
      const nameMatch = block.match(/#{3,4}\s+(.+)/);
      if (!nameMatch) continue;

      const name = nameMatch[1].trim();

      // Find speaker image: prefer image whose alt text contains the speaker's name
      const allImages = [...block.matchAll(/!\[([^\]]*)\]\(([^)]*(?:\([^)]*\)[^)]*)*)\)/g)];
      const nameWords = name.toLowerCase().split(/\s+/);
      const nameMatchedImg = allImages.find((m) => {
        const alt = m[1].toLowerCase();
        return nameWords.some((w) => w.length > 2 && alt.includes(w));
      });
      const imgMatch = nameMatchedImg || (allImages.length > 0 ? allImages[allImages.length - 1] : null);
      const imageUrl = imgMatch ? imgMatch[2] : null;

      // Role is the line right after the name heading
      const afterName = block.slice(block.indexOf(nameMatch[0]) + nameMatch[0].length).trim();
      const roleLines = afterName.split("\n").map((l) => l.trim()).filter(Boolean);
      // Role is typically bold, e.g. **Business Unit Manager...**
      const role = (roleLines[0] || "").replace(/^\*\*|\*\*$/g, "");

      // Bio is everything after role, stripping CTA/link artifacts and markdown formatting
      const bio = roleLines.slice(1)
        .filter((l) => {
          if (/^[\[\](){}<>]+$/.test(l)) return false;
          if (/^\[?!\[/.test(l)) return false;
          if (/^\[(?:Read more|Register|Save|Watch|Discover|Subscribe|Reserve|Reg[ií]strate|Reserva|Suscri)[^\]]*\]\(/.test(l)) return false;
          if (/^(?:Read more|Register here|Save your spot|Watch|Discover|Subscribe|Reg[ií]strate aqu[ií]|Reserva tu plaza|Suscribirse)$/i.test(l)) return false;
          // Filter out empty links [](url) and HubSpot CTA/redirect URLs
          if (/^\[?\]\(/.test(l)) return false;
          if (/qbdgroup\.com\/hs\/cta/i.test(l)) return false;
          if (/encryptedPayload=/i.test(l)) return false;
          if (/cta-service-cms/i.test(l)) return false;
          if (/web-interactives\/public/i.test(l)) return false;
          if (/webInteractiveContentId=/i.test(l)) return false;
          if (/portalId=\d+/i.test(l)) return false;
          return true;
        })
        .join(" ")
        .replace(/\*\*/g, "")
        .replace(/(?<!\w)\*(?!\w)/g, "")
        .trim();

      speakers.push({ name, role, bio, imageUrl });
    }
  }

  // --- About the hosts ---
  let aboutTheHosts = "";
  const hostsBody = extractSectionBody(cleaned_content, HOSTS_PATTERN);
  if (hostsBody) {
    aboutTheHosts = cleanMarkdownBody(hostsBody);
  }

  // --- About the partner (e.g. About Capital Cell) ---
  let aboutPartner = "";
  let partnerTitle = "";
  const partnerHeadingMatch = cleaned_content.match(PARTNER_PATTERN);
  if (partnerHeadingMatch) {
    partnerTitle = partnerHeadingMatch[0].replace(/^#{2,4}\s+/, "").trim();
    const partnerBody = extractSectionBody(cleaned_content, PARTNER_PATTERN);
    if (partnerBody) {
      // Keep partner logos (images) and links; only strip CTA-style lines
      aboutPartner = partnerBody
        .replace(/^\[(?:Register here|Learn more|Watch on demand)\]\([^)]*\)\s*$/gim, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }
  }

  // --- Key takeaways ---
  const keyTakeaways: string[] = [];
  const takeBody = extractSectionBody(cleaned_content, TAKEAWAYS_PATTERN);
  if (takeBody) {
    for (const line of takeBody.split("\n")) {
      const m = line.trim().match(/^(?:[-*]|\d+[.)])\s+(.*)$/);
      if (m) keyTakeaways.push(m[1].trim());
    }
  }

  // --- Our support ---
  let ourSupport = "";
  const supportBody = extractSectionBody(cleaned_content, SUPPORT_PATTERN);
  if (supportBody) {
    ourSupport = cleanMarkdownBody(supportBody);
  }

  // --- Generic body fallback ---
  // When structured sections are mostly empty, extract the main content body
  let genericBody = "";
  const hasStructured = problemFraming || whatYoullLearn.length > 0 || agenda.length > 0;
  if (!hasStructured) {
    // Find end markers
    const endPatterns = [SPEAKERS_PATTERN, SUPPORT_PATTERN, /#{2,4}\s+Stay ahead/i, /#{2,4}\s+Upcoming/i];
    const endIdx = Math.min(
      ...endPatterns.map((p) => findSection(cleaned_content, p)).filter((i) => i > -1),
      cleaned_content.length
    );

    // Find meaningful ## headings, skipping form-related ones
    const skipHeadings = /^##\s+(?:Watch here|Register|Sign up|Stay ahead|Upcoming|Our support|Speaker|Registrarse|Ponentes?|Nuestro soporte)/i;
    const h2Regex = /\n(## [^\n]+)/g;
    let firstContentH2Idx = -1;
    let match: RegExpExecArray | null;
    while ((match = h2Regex.exec(cleaned_content)) !== null) {
      const heading = match[1].trim();
      if (!skipHeadings.test(heading)) {
        firstContentH2Idx = match.index + 1; // +1 to skip the \n
        break;
      }
    }

    if (firstContentH2Idx > 0 && endIdx > firstContentH2Idx) {
      let body = cleaned_content.slice(firstContentH2Idx, endIdx);
      // Strip HubSpot form fields and junk
      body = body
        .replace(/^[A-Za-zÀ-ÿ ]+\\\*\s*$/gm, "")
        .replace(/^(?:Please Select|Selecciona)[^\n]*$/gm, "")
        .replace(/^(?:Afghanistan)[^\n]*$/gm, "")
        .replace(/^QbD Group (?:is committed to protecting|se compromete a proteger)[^\n]*$/gm, "")
        .replace(/^- (?:Yes, I agree|I consent|S[ií], acepto|Acepto la)[^\n]*$/gm, "")
        .replace(/^(?:Content Title|Content Type|Content Industry|Content Division|Select Sessions|Would you like|Role|Department|Industry|Country|Cargo|Departamento|Pa[ií]s|Nombre de la empresa|Email profesional|Division \(level 2\)|¿Necesitas informaci[oó]n[^\n]*)\s*$/gm, "")
        .replace(/^\\\*\s*$/gm, "")
        .replace(/!\[(?:Circles|mobile-shape|Circles_quote|Circles-banner|Círculos_cita)[^\]]*\]\([^)]*\)\s*/g, "")
        .replace(/^\[(?:Watch on demand|Watch (?:the )?webinar on demand|Register here|Learn more|PMCF for Medical Devices|Subscribe to our newsletter|Reserve you? spot|Reg[ií]strate aqu[ií]|M[aá]s informaci[oó]n|Reserva tu plaza|Suscribirse a la newsletter)\]\([^)]*\)\s*$/gm, "")
        .replace(/\[?!\[Calander_icon[^\]]*\]\([^)]*\)\]?\([^)]*\)?\s*/g, "")
        // Remove entire "## Watch here" / "## Registrarse" form sections if somehow included
        .replace(/## (?:Watch here|Registrarse)[\s\S]*?(?=\n## |\n$)/gi, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      body = cleanMarkdownBody(body);
      if (body.length > 100) {
        genericBody = body;
      }
    }
  }

  return {
    meta,
    subtitle,
    introHtml: "",
    problemFraming,
    whatYoullLearn,
    keyTakeaways,
    whoShouldAttend: { title: whoTitle, items: whoItems },
    speakers,
    agenda,
    aboutTheHosts,
    ourSupport,
    aboutPartner,
    partnerTitle,
    genericBody,
  };
}
